/**
 * Canvas Store - Zustand
 *
 * Manages canvas state including:
 * - Canvas data (Fabric.js objects)
 * - Selected tool and settings
 * - Drawing parameters (color, brush size)
 * - Undo/redo history
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  FabricCanvasData,
  MarqueeShape,
  RuntimeCanvasTool,
  ShapeRuntimeTool,
  TypeRuntimeTool,
} from '@canvas/shared-types';
import { isShapeRuntimeTool, isTypeRuntimeTool } from '@canvas/shared-types';

/** Runtime Fabric interaction mode (aligned with TOOL_ID_TO_RUNTIME). */
export type CanvasTool = RuntimeCanvasTool;

export interface CanvasHistoryItem {
  canvasData: FabricCanvasData;
  timestamp: number;
}

export interface CanvasStore {
  // Canvas state
  canvasData: FabricCanvasData | null;
  isDirty: boolean;
  isLoading: boolean;
  error: string | null;

  // Tool state
  selectedTool: CanvasTool;
  /** Last shape chosen in the U flyout (for U shortcut + strip icon). */
  lastShapeTool: ShapeRuntimeTool;
  /** Last type tool in the T flyout. */
  lastTypeTool: TypeRuntimeTool;
  selectedColor: string;
  gradientToColor: string;
  brushSize: number;
  opacity: number;
  /** Regular polygon side count (3–12) */
  polygonSides: number;
  /** Marquee tool shape */
  marqueeShape: MarqueeShape;
  /**
   * Touch / UI stand-in for Shift while selecting (marquee + lasso).
   * When true, new selections add to the current selection.
   */
  additiveSelect: boolean;

  // History
  history: CanvasHistoryItem[];
  historyIndex: number;

  // Actions
  setCanvasData: (data: FabricCanvasData) => void;
  setTool: (tool: CanvasTool) => void;
  setLastShapeTool: (tool: ShapeRuntimeTool) => void;
  setLastTypeTool: (tool: TypeRuntimeTool) => void;
  setSelectedColor: (color: string) => void;
  setGradientToColor: (color: string) => void;
  setBrushSize: (size: number) => void;
  setOpacity: (opacity: number) => void;
  setPolygonSides: (sides: number) => void;
  setMarqueeShape: (shape: MarqueeShape) => void;
  setAdditiveSelect: (additive: boolean) => void;

  // History management
  addToHistory: (canvasData: FabricCanvasData) => void;
  undo: () => FabricCanvasData | null;
  redo: () => FabricCanvasData | null;
  jumpToHistory: (index: number) => FabricCanvasData | null;
  clearHistory: () => void;

  // Utilities
  resetCanvas: () => void;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  markDirty: (dirty: boolean) => void;
}

const HISTORY_LIMIT = 50;
const DEFAULT_CANVAS_DATA: FabricCanvasData = {
  version: '5.3.0',
  objects: [],
  width: 1200,
  height: 800,
};

/** Dispatched when undo/redo/jump should reload Fabric from store canvasData. */
export const HISTORY_APPLY_EVENT = 'craftara:history-apply';

function emitHistoryApply(data: FabricCanvasData): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(HISTORY_APPLY_EVENT, { detail: data }));
}

const DEFAULT_COLOR = '#000000';
const DEFAULT_BRUSH_SIZE = 3;
const DEFAULT_OPACITY = 1;

/**
 * Canvas Store
 * Manages all canvas-related state and operations
 */
export const useCanvasStore = create<CanvasStore>()(
  devtools(
    (set, get) => ({
      // Initial state
      canvasData: DEFAULT_CANVAS_DATA,
      isDirty: false,
      isLoading: false,
      error: null,

      selectedTool: 'select' as CanvasTool,
      lastShapeTool: 'rectangle' as ShapeRuntimeTool,
      lastTypeTool: 'text' as TypeRuntimeTool,
      selectedColor: DEFAULT_COLOR,
      gradientToColor: '#0d7377',
      brushSize: DEFAULT_BRUSH_SIZE,
      opacity: DEFAULT_OPACITY,
      polygonSides: 6,
      marqueeShape: 'rect' as MarqueeShape,
      additiveSelect: false,

      history: [
        {
          canvasData: DEFAULT_CANVAS_DATA,
          timestamp: Date.now(),
        },
      ],
      historyIndex: 0,

      /**
       * Set the entire canvas data.
       * Does not mark dirty — callers that edit must call markDirty(true).
       * Loading a project must not look like an unsaved edit.
       */
      setCanvasData: (data: FabricCanvasData) => {
        set({
          canvasData: data,
          error: null,
        });
      },

      /**
       * Set the currently selected tool (see RuntimeCanvasTool / TOOL_STRIP_IDS)
       */
      setTool: (tool: CanvasTool) => {
        if (isShapeRuntimeTool(tool)) {
          set({ selectedTool: tool, lastShapeTool: tool });
        } else if (isTypeRuntimeTool(tool)) {
          set({ selectedTool: tool, lastTypeTool: tool });
        } else {
          set({ selectedTool: tool });
        }
      },

      setLastShapeTool: (tool: ShapeRuntimeTool) => {
        set({ lastShapeTool: tool });
      },

      setLastTypeTool: (tool: TypeRuntimeTool) => {
        set({ lastTypeTool: tool });
      },

      /**
       * Set the drawing color (hex format)
       */
      setSelectedColor: (color: string) => {
        if (!/^#[0-9A-F]{6}$/i.test(color)) {
          set({ error: 'Invalid color format. Use #RRGGBB' });
          return;
        }
        set({ selectedColor: color, error: null });
      },

      setGradientToColor: (color: string) => {
        if (!/^#[0-9A-F]{6}$/i.test(color)) {
          set({ error: 'Invalid color format. Use #RRGGBB' });
          return;
        }
        set({ gradientToColor: color, error: null });
      },

      /**
       * Set brush/stroke size in pixels
       */
      setBrushSize: (size: number) => {
        if (size < 1 || size > 100) {
          set({ error: 'Brush size must be between 1 and 100' });
          return;
        }
        set({ brushSize: size, error: null });
      },

      /**
       * Set opacity (0-1)
       */
      setOpacity: (opacity: number) => {
        if (opacity < 0 || opacity > 1) {
          set({ error: 'Opacity must be between 0 and 1' });
          return;
        }
        set({ opacity, error: null });
      },

      setPolygonSides: (sides: number) => {
        set({
          polygonSides: Math.min(12, Math.max(3, Math.round(sides))),
          error: null,
        });
      },

      setMarqueeShape: (shape: MarqueeShape) => {
        set({ marqueeShape: shape, error: null });
      },

      setAdditiveSelect: (additive: boolean) => {
        set({ additiveSelect: Boolean(additive), error: null });
      },

      /**
       * Add a canvas state to history
       * Maintains a limited history (HISTORY_LIMIT) to prevent memory bloat
       * When we have undone and add new state, discard redo history
       */
      addToHistory: (canvasData: FabricCanvasData) => {
        set((state) => {
          const { history, historyIndex } = state;

          // If we're not at the end of history (user undid something),
          // remove the "redo" portion
          const newHistory = history.slice(0, historyIndex + 1);

          // Add new state
          newHistory.push({
            canvasData: { ...canvasData },
            timestamp: Date.now(),
          });

          // Limit history size to prevent memory issues
          if (newHistory.length > HISTORY_LIMIT) {
            newHistory.shift();
          }

          return {
            history: newHistory,
            historyIndex: newHistory.length - 1,
            isDirty: true,
          };
        });
      },

      /**
       * Undo to previous canvas state
       */
      undo: (): FabricCanvasData | null => {
        const state = get();
        const { history, historyIndex } = state;

        if (historyIndex <= 0) {
          set({ error: 'Nothing to undo' });
          return null;
        }

        const newIndex = historyIndex - 1;
        const previousState = history[newIndex];

        set({
          historyIndex: newIndex,
          canvasData: previousState.canvasData,
          isDirty: true,
          error: null,
        });
        emitHistoryApply(previousState.canvasData);
        return previousState.canvasData;
      },

      /**
       * Redo to next canvas state
       */
      redo: (): FabricCanvasData | null => {
        const state = get();
        const { history, historyIndex } = state;

        if (historyIndex >= history.length - 1) {
          set({ error: 'Nothing to redo' });
          return null;
        }

        const newIndex = historyIndex + 1;
        const nextState = history[newIndex];

        set({
          historyIndex: newIndex,
          canvasData: nextState.canvasData,
          isDirty: true,
          error: null,
        });
        emitHistoryApply(nextState.canvasData);
        return nextState.canvasData;
      },

      /**
       * Jump to an absolute history index (History panel).
       */
      jumpToHistory: (index: number): FabricCanvasData | null => {
        const state = get();
        const { history } = state;
        if (index < 0 || index >= history.length) {
          set({ error: 'Invalid history index' });
          return null;
        }
        if (index === state.historyIndex) {
          return history[index].canvasData;
        }
        const target = history[index];
        set({
          historyIndex: index,
          canvasData: target.canvasData,
          isDirty: true,
          error: null,
        });
        emitHistoryApply(target.canvasData);
        return target.canvasData;
      },

      /**
       * Clear undo/redo history
       * Used when loading a new project
       */
      clearHistory: () => {
        const state = get();
        const currentCanvas = state.canvasData || DEFAULT_CANVAS_DATA;

        set({
          history: [
            {
              canvasData: currentCanvas,
              timestamp: Date.now(),
            },
          ],
          historyIndex: 0,
        });
      },

      /**
       * Reset canvas to blank state
       */
      resetCanvas: () => {
        set({
          canvasData: { ...DEFAULT_CANVAS_DATA },
          selectedTool: 'select',
          lastShapeTool: 'rectangle',
          lastTypeTool: 'text',
          selectedColor: DEFAULT_COLOR,
          gradientToColor: '#0d7377',
          brushSize: DEFAULT_BRUSH_SIZE,
          opacity: DEFAULT_OPACITY,
          isDirty: false,
          error: null,
          history: [
            {
              canvasData: DEFAULT_CANVAS_DATA,
              timestamp: Date.now(),
            },
          ],
          historyIndex: 0,
        });
      },

      /**
       * Set error message
       */
      setError: (error: string | null) => {
        set({ error });
      },

      /**
       * Set loading state
       */
      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      /**
       * Mark canvas as dirty (unsaved changes)
       */
      markDirty: (dirty: boolean) => {
        set({ isDirty: dirty });
      },
    }),
    { name: 'canvas-store' }
  )
);
